import { Toast as T } from "radix-ui";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { IconClose } from "./icons.tsx";

export type ToastTone = "info" | "success" | "danger";

export interface ToastMessage {
  title: string;
  description?: string;
  tone?: ToastTone;
}

interface Item extends ToastMessage {
  id: number;
  open: boolean;
}

type Push = (message: ToastMessage) => void;

const ToastContext = createContext<Push | null>(null);

/** Показать тост: результат скана, ошибка сервера. */
export function useToast(): Push {
  const push = useContext(ToastContext);
  if (!push) throw new Error("useToast вне ToastProvider");
  return push;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Подпись кнопки закрытия и области уведомлений, из каталога строк. */
  closeLabel: string;
  regionLabel: string;
}

let nextId = 1;

export function ToastProvider({ children, closeLabel, regionLabel }: ToastProviderProps) {
  const [items, setItems] = useState<Item[]>([]);

  const push = useCallback<Push>((message) => {
    const id = nextId++;
    // Не больше трёх: очередь сканов не должна закрыть экран тостами.
    setItems((prev) => [...prev.slice(-2), { ...message, id, open: true }]);
  }, []);

  const close = useCallback((id: number) => {
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, open: false } : t)));
    // Узел убирается после анимации закрытия; Radix сам ждёт её конца.
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 1000);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      <T.Provider swipeDirection="right" duration={5000} label={regionLabel}>
        {children}
        {items.map((t) => (
          <T.Root
            key={t.id}
            className="w-toast"
            data-tone={t.tone ?? "info"}
            open={t.open}
            onOpenChange={(open) => {
              if (!open) close(t.id);
            }}
            type={t.tone === "danger" ? "foreground" : "background"}
          >
            <T.Title className="w-toast-title">{t.title}</T.Title>
            {t.description ? <T.Description className="w-toast-description">{t.description}</T.Description> : null}
            <T.Close className="w-btn w-toast-close" data-variant="ghost" data-size="icon" aria-label={closeLabel}>
              <IconClose />
            </T.Close>
          </T.Root>
        ))}
        <T.Viewport className="w-toast-viewport" label={`${regionLabel} ({hotkey})`} />
      </T.Provider>
    </ToastContext.Provider>
  );
}
